import { writable, get } from 'svelte/store'
import type { Writable } from 'svelte/store'
import type { NewsPost } from '@/components/appDriver/newsfeed/types'

const READ_NEWS_STORAGE_KEY = 'driver-newsfeed-read-post-ids'
const CACHED_NEWS_STORAGE_KEY = 'driver-newsfeed-cached-posts'

function loadReadNewsPostIds(): string[] {
  if (typeof window === 'undefined') {
    return []
  }

  const savedPostIds = localStorage.getItem(READ_NEWS_STORAGE_KEY)

  if (!savedPostIds) {
    return []
  }

  try {
    const parsedPostIds = JSON.parse(savedPostIds)
    return Array.isArray(parsedPostIds) ? parsedPostIds : []
  } catch (error) {
    console.error('Cannot parse read news post ids', error)
    return []
  }
}

function loadCachedNewsPosts(): NewsPost[] {
  if (typeof window === 'undefined') {
    return []
  }

  const savedPosts = localStorage.getItem(CACHED_NEWS_STORAGE_KEY)

  if (!savedPosts) {
    return []
  }

  try {
    const parsedPosts = JSON.parse(savedPosts)
    return Array.isArray(parsedPosts) ? parsedPosts : []
  } catch (error) {
    console.error('Cannot parse cached news posts', error)
    return []
  }
}

export const selectedNewsPostId: Writable<string | null> = writable(null)

export const selectedNewsPost: Writable<NewsPost | null> = writable(null)

const initialCachedNewsPosts = loadCachedNewsPosts()

export const cachedNewsPosts: Writable<NewsPost[]> = writable(initialCachedNewsPosts)

export const hasLoadedNewsPosts: Writable<boolean> = writable(initialCachedNewsPosts.length > 0)

export const readNewsPostIds: Writable<string[]> = writable(loadReadNewsPostIds())

readNewsPostIds.subscribe((postIds) => {
  if (typeof window === 'undefined') {
    return
  }

  localStorage.setItem(READ_NEWS_STORAGE_KEY, JSON.stringify(postIds))
})

cachedNewsPosts.subscribe((posts) => {
  if (typeof window === 'undefined') {
    return
  }

  localStorage.setItem(CACHED_NEWS_STORAGE_KEY, JSON.stringify(posts))
})

export function markNewsPostAsRead(postId: string): void {
  const currentPostIds = get(readNewsPostIds)

  if (currentPostIds.includes(postId)) {
    return
  }

  readNewsPostIds.set([...currentPostIds, postId])
}

export function isNewsPostRead(postId: string, readPostIds: string[]): boolean {
  return readPostIds.includes(postId)
}

export function setSelectedNewsPost(post: NewsPost | null): void {
  selectedNewsPost.set(post)
  selectedNewsPostId.set(post?.id ?? null)
}

export function setCachedNewsPosts(posts: NewsPost[]): void {
  cachedNewsPosts.set(posts)
  hasLoadedNewsPosts.set(true)
}

export function clearCachedNewsPosts(): void {
  cachedNewsPosts.set([])
  hasLoadedNewsPosts.set(false)
}
